import React, { useState, useEffect, useCallback } from 'react';
import { Key, Plus, Trash2, Check, AlertCircle, Loader2, Settings, ToggleLeft, ToggleRight, RefreshCw, ArrowRight } from 'lucide-react';
import { API } from '../../App';

const card = 'bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4';
const inputCls = 'w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none min-h-[44px]';
const btnPrimary = 'px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded hover:bg-[#E63E00] transition-colors disabled:opacity-50';
const btnDanger = 'px-3 py-2 min-h-[44px] bg-[#A61E17] text-white text-sm rounded hover:bg-[#8B1912] transition-colors disabled:opacity-50';
const btnSecondary = 'px-3 py-2 min-h-[44px] border border-[#2A2A2A] text-[#A0A0A0] text-sm rounded hover:border-[#FF4D00] hover:text-[#F5F1E8] transition-colors disabled:opacity-50';
const selectCls = 'bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] focus:border-[#FF4D00] focus:outline-none min-h-[44px]';

const COMMON_PROVIDERS = [
  { slug: 'openrouter', name: 'OpenRouter' },
  { slug: 'anthropic', name: 'Anthropic' },
  { slug: 'google-vertex', name: 'Google Vertex' },
  { slug: 'azure-openai', name: 'Azure OpenAI' },
  { slug: 'deepseek', name: 'DeepSeek' },
  { slug: 'kimi', name: 'Kimi' },
  { slug: 'perplexity', name: 'Perplexity' },
];

function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function MultiProviderKeys({ workspaceId, onNavigateTab }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [selectedProvider, setSelectedProvider] = useState('');
  const [customName, setCustomName] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testingSlug, setTestingSlug] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [deletingSlug, setDeletingSlug] = useState(null);

  // OpenRouter provisioning state
  const [orConfig, setOrConfig] = useState(null);
  const [orLoading, setOrLoading] = useState(true);
  const [autoProvision, setAutoProvision] = useState(false);
  const [runtimeKey, setRuntimeKey] = useState('');
  const [managementKey, setManagementKey] = useState('');
  const [savingOrKey, setSavingOrKey] = useState(false);
  const [savedOrKey, setSavedOrKey] = useState(false);
  const [togglingAP, setTogglingAP] = useState(false);
  const [switchWarning, setSwitchWarning] = useState(null);

  // Model sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');

  const fetchProviders = useCallback(async () => {
    try {
      const r = await API.get(`/api/workspaces/${workspaceId}/providers`);
      setProviders(r.data.providers || []);
    } catch { setProviders([]); }
    setLoading(false);
  }, [workspaceId]);

  const fetchOrConfig = useCallback(async () => {
    try {
      const r = await API.get(`/api/workspaces/${workspaceId}/or-provisioning`);
      setOrConfig(r.data);
      setAutoProvision(r.data.auto_provision || false);
    } catch { setOrConfig({ auto_provision: false, management_key_configured: false, runtime_key_configured: false }); }
    setOrLoading(false);
  }, [workspaceId]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const r = await API.get('/api/models/sync-status');
      setSyncStatus(r.data);
    } catch { setSyncStatus(null); }
  }, []);

  useEffect(() => {
    fetchProviders();
    fetchOrConfig();
    fetchSyncStatus();
  }, [fetchProviders, fetchOrConfig, fetchSyncStatus]);

  const isCustom = selectedProvider === 'custom';
  const providerName = isCustom ? customName : (COMMON_PROVIDERS.find(p => p.slug === selectedProvider)?.name || '');
  const providerSlug = isCustom ? customSlug : selectedProvider;

  // Filter out openrouter from the "Add Provider" dropdown since it has its own section
  const addableProviders = COMMON_PROVIDERS.filter(p => p.slug !== 'openrouter');
  // Non-openrouter providers in the list
  const otherProviders = providers.filter(p => p.slug !== 'openrouter');

  const resetForm = () => {
    setSelectedProvider('');
    setCustomName('');
    setCustomSlug('');
    setApiKey('');
    setShowForm(false);
    setError('');
  };

  const handleSave = async () => {
    if (!providerSlug || !apiKey.trim()) {
      setError('Provider and API key are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await API.put(`/api/workspaces/${workspaceId}/providers`, {
        slug: providerSlug,
        name: providerName || providerSlug,
        api_key: apiKey,
        key_type: 'runtime',
      });
      resetForm();
      await fetchProviders();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save provider');
    }
    setSaving(false);
  };

  const handleDelete = async (slug) => {
    setDeletingSlug(slug);
    try {
      await API.delete(`/api/workspaces/${workspaceId}/providers/${slug}`);
      await fetchProviders();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete provider');
    }
    setDeletingSlug(null);
  };

  const handleTest = async (provider) => {
    setTestingSlug(provider.slug);
    setTestResult({});
    try {
      await API.put(`/api/workspaces/${workspaceId}/providers`, {
        slug: provider.slug,
        name: provider.name,
        api_key: '',
        key_type: provider.key_type,
        test_only: true,
      });
      setTestResult({ [provider.slug]: 'valid' });
    } catch {
      setTestResult({ [provider.slug]: 'invalid' });
    }
    setTestingSlug(null);
  };

  // --- OpenRouter key save ---
  const saveOrKey = async () => {
    const key = autoProvision ? managementKey : runtimeKey;
    if (!key.trim()) return;
    setSavingOrKey(true);
    setError('');
    try {
      if (autoProvision) {
        await API.put(`/api/workspaces/${workspaceId}/or-provisioning`, {
          management_key: key,
          auto_provision: true,
        });
      } else {
        await API.put(`/api/workspaces/${workspaceId}/or-provisioning`, {
          runtime_key: key,
        });
      }
      setRuntimeKey('');
      setManagementKey('');
      await fetchOrConfig();
      setSavedOrKey(true);
      setTimeout(() => setSavedOrKey(false), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save key');
    }
    setSavingOrKey(false);
  };

  // --- Toggle auto-provisioning ---
  const handleToggleAP = async (newValue) => {
    // Warn if switching and the other key type is already configured
    if (newValue && orConfig?.runtime_key_configured) {
      setSwitchWarning('runtime');
      return;
    }
    if (!newValue && orConfig?.management_key_configured) {
      setSwitchWarning('management');
      return;
    }
    await doToggleAP(newValue);
  };

  const doToggleAP = async (newValue) => {
    setSwitchWarning(null);
    setTogglingAP(true);
    setError('');
    try {
      await API.put(`/api/workspaces/${workspaceId}/or-provisioning`, {
        auto_provision: newValue,
      });
      await fetchOrConfig();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to toggle auto-provisioning');
    }
    setTogglingAP(false);
  };

  // --- Model sync ---
  const handleSync = async () => {
    setSyncing(true);
    setSyncError('');
    try {
      await API.post('/api/admin/models/sync');
      await fetchSyncStatus();
    } catch (e) {
      setSyncError(e.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  };

  if (loading || orLoading) return <div className="text-center py-10 text-[#A0A0A0]">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold font-['Barlow_Condensed'] text-[#F5F1E8] flex items-center gap-2">
          <Settings className="w-5 h-5 text-[#FF4D00]" />
          Providers
        </h2>
        <p className="text-xs text-[#A0A0A0] mt-1">
          Configure API keys and providers for your workspace. OpenRouter is the primary provider for fray execution.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-[#A61E17] text-sm bg-[#A61E17]/10 border border-[#A61E17]/30 rounded px-3 py-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* =============== OPENROUTER SECTION =============== */}
      <div className="border border-[#2A2A2A] rounded-lg overflow-hidden">
        <div className="bg-[#141414] px-4 py-3 border-b border-[#2A2A2A] flex items-center gap-3">
          <Key className="w-5 h-5 text-[#FF4D00]" />
          <div>
            <span className="text-sm font-bold text-[#F5F1E8] font-['Barlow_Condensed']">OpenRouter</span>
            <span className="text-[10px] text-[#A0A0A0] ml-2">Primary provider for fray execution</span>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Auto-provisioning toggle */}
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-mono text-[#F5F1E8]">Auto-provisioning</span>
              <p className="text-[10px] text-[#A0A0A0] mt-0.5">
                {autoProvision
                  ? 'Each workspace member gets their own OpenRouter sub-key. Individual usage tracked separately.'
                  : 'All workspace users share one OpenRouter key. Usage billed to your OpenRouter account.'}
              </p>
            </div>
            <button
              onClick={() => handleToggleAP(!autoProvision)}
              disabled={togglingAP}
              className="text-[#A0A0A0] hover:text-[#F5F1E8] flex-shrink-0 ml-4"
              title={autoProvision ? 'Switch to shared runtime key' : 'Switch to per-user provisioned keys'}
            >
              {autoProvision ? (
                <ToggleRight className="w-8 h-8 text-[#00D4FF]" />
              ) : (
                <ToggleLeft className="w-8 h-8" />
              )}
            </button>
          </div>

          {/* Switch warning dialog */}
          {switchWarning && (
            <div className="bg-[#F2A900]/10 border border-[#F2A900]/30 rounded px-3 py-2 text-sm">
              <p className="text-[#F2A900] text-xs font-bold mb-1">
                {switchWarning === 'runtime'
                  ? 'A runtime key is already configured.'
                  : 'A management key is already configured.'}
              </p>
              <p className="text-[#A0A0A0] text-xs mb-2">
                Switching modes will change which key is used for fray execution. The existing key will remain stored.
              </p>
              <div className="flex gap-2">
                <button onClick={() => doToggleAP(switchWarning === 'runtime')} className={btnPrimary + ' text-xs !py-1 !min-h-0'}>
                  Switch anyway
                </button>
                <button onClick={() => setSwitchWarning(null)} className={btnSecondary + ' text-xs !py-1 !min-h-0'}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Conditional key input based on mode */}
          {!autoProvision ? (
            /* Runtime Key mode */
            <div className={card}>
              <div className="flex items-center gap-3 mb-3">
                <Key className="w-4 h-4 text-[#00D4FF]" />
                <div>
                  <span className="text-sm font-mono text-[#F5F1E8]">Runtime API Key</span>
                  <p className="text-[10px] text-[#A0A0A0]">All workspace users share this OpenRouter key. Usage billed to your OpenRouter account.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-2 h-2 rounded-full ${orConfig?.runtime_key_configured ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />
                <span className="text-xs font-mono text-[#A0A0A0]">
                  {orConfig?.runtime_key_configured
                    ? <span className="text-[#22C55E]">{orConfig.runtime_key_preview}</span>
                    : <span className="text-[#EF4444]">Not configured — workspace users need individual BYOK keys</span>
                  }
                </span>
              </div>
              <div className="flex gap-2">
                <input type="password" value={runtimeKey} onChange={e => setRuntimeKey(e.target.value)}
                  placeholder="sk-or-v1-... (OpenRouter Runtime API Key)" className={inputCls} />
                <button onClick={saveOrKey} disabled={savingOrKey || !runtimeKey.trim()} className={btnPrimary}>
                  {savingOrKey ? <Loader2 className="w-4 h-4 animate-spin" /> : savedOrKey ? <Check className="w-4 h-4" /> : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            /* Management Key mode */
            <div className={card}>
              <div className="flex items-center gap-3 mb-3">
                <Key className="w-4 h-4 text-[#F2A900]" />
                <div>
                  <span className="text-sm font-mono text-[#F5F1E8]">Management API Key</span>
                  <p className="text-[10px] text-[#A0A0A0]">Each workspace member gets their own OpenRouter sub-key. Individual usage tracked separately.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-2 h-2 rounded-full ${orConfig?.management_key_configured ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />
                <span className="text-xs font-mono text-[#A0A0A0]">
                  Management key: {orConfig?.management_key_configured
                    ? <span className="text-[#22C55E]">{orConfig.management_key_preview}</span>
                    : <span className="text-[#EF4444]">Not configured</span>
                  }
                </span>
              </div>
              <div className="flex gap-2">
                <input type="password" value={managementKey} onChange={e => setManagementKey(e.target.value)}
                  placeholder="sk-or-v1-... (OpenRouter Management API Key)" className={inputCls} />
                <button onClick={saveOrKey} disabled={savingOrKey || !managementKey.trim()} className={btnPrimary}>
                  {savingOrKey ? <Loader2 className="w-4 h-4 animate-spin" /> : savedOrKey ? <Check className="w-4 h-4" /> : 'Save'}
                </button>
              </div>
              <p className="text-[10px] text-[#A0A0A0] mt-1">
                Get your management key from <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noopener noreferrer" className="text-[#00D4FF] underline">openrouter.ai/settings/keys</a>
              </p>
            </div>
          )}

          {/* Model Sync */}
          <div className="border-t border-[#2A2A2A] pt-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-mono text-[#F5F1E8]">Model Sync</span>
                <p className="text-[10px] text-[#A0A0A0] mt-0.5">
                  {syncStatus?.last_sync
                    ? <>Last synced: <span className="text-[#00D4FF]">{new Date(syncStatus.last_sync).toLocaleString()}</span> — {syncStatus.active_models} active models</>
                    : 'Models have not been synced yet'}
                </p>
              </div>
              <button onClick={handleSync} disabled={syncing} className={btnSecondary + ' flex items-center gap-2 text-xs'}>
                {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Sync Available Models
              </button>
            </div>
            {syncError && (
              <p className="text-xs text-[#A61E17] mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {syncError}
              </p>
            )}
            {onNavigateTab && (
              <button
                onClick={() => onNavigateTab('models')}
                className="text-xs text-[#00D4FF] hover:text-[#F5F1E8] mt-2 flex items-center gap-1 transition-colors"
              >
                Manage which models your team can use <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* =============== OTHER PROVIDERS =============== */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold font-['Barlow_Condensed'] text-[#F5F1E8]">Other Providers</h3>
          <p className="text-xs text-[#A0A0A0] mt-0.5">Add API keys for direct provider access beyond OpenRouter.</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className={btnPrimary + ' flex items-center gap-2 text-xs'}>
            <Plus className="w-4 h-4" /> Add Provider
          </button>
        )}
      </div>

      {/* Add Provider Form */}
      {showForm && (
        <div className={card + ' space-y-4'}>
          <h3 className="text-sm font-bold text-[#F5F1E8] font-['Barlow_Condensed']">Add Provider</h3>

          <div>
            <label className="block text-xs text-[#A0A0A0] mb-1">Provider</label>
            <select
              value={selectedProvider}
              onChange={(e) => {
                setSelectedProvider(e.target.value);
                if (e.target.value !== 'custom') {
                  setCustomName('');
                  setCustomSlug('');
                }
              }}
              className={selectCls + ' w-full'}
            >
              <option value="">Select provider...</option>
              {addableProviders.map(p => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
              <option value="custom">Custom...</option>
            </select>
          </div>

          {isCustom && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#A0A0A0] mb-1">Provider Name</label>
                <input
                  value={customName}
                  onChange={(e) => {
                    setCustomName(e.target.value);
                    setCustomSlug(slugify(e.target.value));
                  }}
                  placeholder="My Provider"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-xs text-[#A0A0A0] mb-1">Slug</label>
                <input
                  value={customSlug}
                  onChange={(e) => setCustomSlug(slugify(e.target.value))}
                  placeholder="my-provider"
                  className={inputCls}
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs text-[#A0A0A0] mb-1">API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-..."
              className={inputCls}
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button onClick={handleSave} disabled={saving || !providerSlug || !apiKey.trim()} className={btnPrimary + ' flex items-center gap-2'}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Provider
            </button>
            <button onClick={resetForm} className={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      {/* Other Provider List */}
      {otherProviders.length === 0 && !showForm && (
        <div className={card + ' text-center py-6'}>
          <Key className="w-6 h-6 text-[#2A2A2A] mx-auto mb-2" />
          <p className="text-[#A0A0A0] text-sm">No additional providers configured.</p>
        </div>
      )}

      <div className="space-y-3">
        {otherProviders.map((p) => (
          <div key={p.slug} className={card + ' flex items-center justify-between'}>
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <Key className="w-5 h-5 text-[#FF4D00] flex-shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-[#F5F1E8]">{p.name}</span>
                  <span className="text-xs font-mono text-[#666]">{p.slug}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    p.status === 'active' ? 'bg-green-900/30 text-green-400' : 'bg-[#A61E17]/20 text-[#A61E17]'
                  }`}>
                    {p.status}
                  </span>
                </div>
                <span className="text-xs font-mono text-[#666]">{p.key_preview}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {testResult[p.slug] === 'valid' && <Check className="w-4 h-4 text-green-400" />}
              {testResult[p.slug] === 'invalid' && <AlertCircle className="w-4 h-4 text-[#A61E17]" />}
              <button
                onClick={() => handleTest(p)}
                disabled={testingSlug === p.slug}
                className={btnSecondary + ' text-xs'}
              >
                {testingSlug === p.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Test'}
              </button>
              <button
                onClick={() => handleDelete(p.slug)}
                disabled={deletingSlug === p.slug}
                className={btnDanger + ' text-xs flex items-center gap-1'}
              >
                {deletingSlug === p.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
